/**
 * O domínio da tela de parametrização (`TP-051`, T10/T11 — `US-037`): o
 * responsável pela parametrização importa o catálogo exportado da tela de
 * parametrização do IW, vê onde ele diverge do contrato, e confirma o
 * vínculo (de-para) código do IW × item do contrato.
 *
 * A seção `*Api` é a forma CRUA, escrita à mão a partir de
 * `contracts/openapi/anery-api.v1.json` (schemas `ReconciliacaoResponse`,
 * `DivergenciaResponse`, `PropostaResponse`, `ItemDaFilaResponse`,
 * `ConfirmarDeParaRequest`) — a geração de `@anery/api-contracts` ainda não
 * cobre este bloco neste worktree, mesma situação de `features/contratos`.
 *
 * ATENÇÃO (diferente do resto da API, `DI24` do TP-051 — `ponto`/
 * `modalidade` usam o NOME DO MEMBRO C# em inglês): os campos `tipo` da
 * divergência e `origem` da fila JÁ CHEGAM em pt-BR. `Divergencia.Tipo` e
 * `ItemDaFilaDePara.Origem` (`Anery.Application.Parametrizacao`,
 * `ReconciliarCatalogo.cs`/`FilaDePara.cs`) são strings de domínio escritas
 * à mão, não token de enum C# — então não há tradução de vocabulário
 * aqui, só tipagem e rótulo de exibição.
 */

export type TipoDeDivergencia =
  | 'codigo_inativo'
  | 'valor_divergente'
  | 'unidade_divergente'
  | 'sem_correspondencia_no_contrato'
  | 'item_ausente_no_iw'
  | 'sem_preco_no_iw';

export const TIPOS_DE_DIVERGENCIA: readonly TipoDeDivergencia[] = [
  'codigo_inativo',
  'valor_divergente',
  'unidade_divergente',
  'sem_correspondencia_no_contrato',
  'item_ausente_no_iw',
  'sem_preco_no_iw',
];

export const ROTULOS_DE_TIPO_DE_DIVERGENCIA: Record<TipoDeDivergencia, string> = {
  codigo_inativo: 'Código inativo nesta competência',
  valor_divergente: 'Valor divergente',
  unidade_divergente: 'Unidade divergente',
  sem_correspondencia_no_contrato: 'Sem correspondência no contrato',
  item_ausente_no_iw: 'Item do contrato ausente no catálogo do IW',
  sem_preco_no_iw: 'Sem preço na tabela do IW',
};

export type OrigemDoItemDaFila = 'proposta' | 'divergencia';

export const ROTULOS_DE_ORIGEM_DA_FILA: Record<OrigemDoItemDaFila, string> = {
  proposta: 'Proposto — código igual ao do contrato',
  divergencia: 'Sem proposta — revise manualmente',
};

export interface DivergenciaApi {
  readonly tipo: string;
  readonly codigo: string;
  readonly descricao: string | null;
  readonly detalhe: string;
}

export interface PropostaDeParaApi {
  readonly codigoIw: string;
  readonly itemContratualId: string;
  readonly descricao: string;
}

export interface LinhaDaComparacaoApi {
  readonly situacao: string;
  readonly vinculado: boolean;
  readonly codigoIw: string | null;
  readonly descricaoIw: string | null;
  readonly valorIw: number | null;
  readonly itemContratualId: string | null;
  readonly codigoNoContrato: string | null;
  readonly descricaoNoContrato: string | null;
  readonly valorNoContrato: number | null;
  readonly unidadeNoContrato: string | null;
}

export interface ReconciliacaoApi {
  readonly avaliacaoId: string;
  readonly cobertura: number;
  readonly divergencias: readonly DivergenciaApi[] | null;
  readonly propostas: readonly PropostaDeParaApi[] | null;
  readonly criadoEm: string;
  readonly linhas?: readonly LinhaDaComparacaoApi[] | null;
}

/** Um item do contrato para escolher no vínculo manual (`GET /contratos/{id}/itens`). */
export interface ItemDoContratoApi {
  readonly id: string;
  readonly code: string | null;
  readonly description: string | null;
  readonly value: number;
}

export interface ItemDaFilaDeParaApi {
  readonly codigoIw: string | null;
  readonly itemContratualIdSugerido: string | null;
  readonly descricao: string | null;
  readonly origem: string;
  readonly vezesSemCobertura: number;
}

// -------------------------------------------------------- domínio do front

export interface Divergencia {
  readonly tipo: TipoDeDivergencia | null;
  readonly tipoBruto: string;
  readonly codigo: string;
  readonly descricao: string | null;
  readonly detalhe: string;
}

export interface PropostaDePara {
  readonly codigoIw: string;
  readonly itemContratualId: string;
  readonly descricao: string;
}

/** A situação de uma linha da comparação contrato × IW (`TP-071`, T11). */
export type SituacaoDaComparacao = 'igual' | 'preco_diverge' | 'sem_preco_no_iw' | 'so_no_iw' | 'so_no_contrato';

export const ROTULOS_DA_SITUACAO: Record<SituacaoDaComparacao, string> = {
  igual: 'Igual',
  preco_diverge: 'Preço diverge',
  sem_preco_no_iw: 'Sem preço no IW',
  so_no_iw: 'Só no IW',
  so_no_contrato: 'Só no contrato',
};

/** O que fazer, por situação: quem age e onde. */
export const O_QUE_FAZER: Record<SituacaoDaComparacao, string> = {
  igual: 'Nada: o IW cobra o que o contrato diz.',
  preco_diverge:
    'Confira o preço no contrato. Se o contrato estiver certo, a tabela do IW está errada: avise a parametrização do IW.',
  sem_preco_no_iw: 'O IW não deixa o orçamento sair sem preço: peça à parametrização do IW que precifique o item.',
  so_no_iw:
    'O IW cobra um item que este contrato não tem. Se ele existe no contrato com outro código, ligue ao item; se não, falta a fonte (um aditivo?) ou o item está fora do contrato.',
  so_no_contrato: 'O contrato tem o item e o IW não o cobra: avise a parametrização do IW.',
};

/** O mesmo código nos dois lados, ainda sem vínculo: confirmar é o clique humano do de-para. */
export const A_VINCULAR =
  'O mesmo código está no contrato e no IW, mas o vínculo ainda não foi confirmado. Confira e clique em Vincular: só então o orçamento é conferido por esse item.';

export interface LinhaDaComparacao {
  readonly situacao: SituacaoDaComparacao;
  readonly vinculado: boolean;
  readonly codigoIw: string | null;
  readonly descricaoIw: string | null;
  readonly valorIw: number | null;
  readonly itemContratualId: string | null;
  readonly codigoNoContrato: string | null;
  readonly descricaoNoContrato: string | null;
  readonly valorNoContrato: number | null;
  readonly unidadeNoContrato: string | null;
}

export interface Reconciliacao {
  readonly avaliacaoId: string;
  readonly cobertura: number;
  readonly divergencias: readonly Divergencia[];
  readonly propostas: readonly PropostaDePara[];
  readonly criadoEm: string;
  readonly linhas: readonly LinhaDaComparacao[];
}

export interface ItemDoContrato {
  readonly id: string;
  readonly codigo: string;
  readonly descricao: string;
  readonly valor: number;
}

export interface ItemDaFilaDePara {
  readonly codigoIw: string | null;
  readonly itemContratualIdSugerido: string | null;
  readonly descricao: string | null;
  readonly origem: OrigemDoItemDaFila | null;
  readonly origemBruta: string;
  readonly vezesSemCobertura: number;
}

export function ehTipoDeDivergenciaConhecido(valor: string): valor is TipoDeDivergencia {
  return (TIPOS_DE_DIVERGENCIA as readonly string[]).includes(valor);
}

export function ehSituacaoConhecida(valor: string): valor is SituacaoDaComparacao {
  return valor in ROTULOS_DA_SITUACAO;
}

export function ehOrigemConhecida(valor: string): valor is OrigemDoItemDaFila {
  return valor === 'proposta' || valor === 'divergencia';
}
